import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import ClientService from '../service/client.services'
import {TCreateClient, TClient, TUpdateClient} from '../types/client.type'
import {ErrorHelper} from '../types/app.type'

export const useGetClients = () => {
  const {data, isPending, isSuccess} = useQuery({
    queryKey: ['clients'],
    queryFn: () => ClientService.getUsersClients(),
    staleTime: Infinity,
  })

  return {data, isPending, isSuccess}
}

export const useCreateClient = () => {
  const queryClient = useQueryClient()
  const {
    mutateAsync: createClient,
    isPending,
    isSuccess,
    error,
  } = useMutation<TClient, ErrorHelper, TCreateClient>({
    mutationFn: (clientData: TCreateClient) =>
      ClientService.createClient(clientData),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['clients']})
    },
  })

  return {createClient, isPending, isSuccess, error}
}

export const useUpdateClient = (clientId?: string) => {
  const queryClient = useQueryClient()
  const {
    mutateAsync: updateClient,
    isPending,
    isSuccess,
    error,
  } = useMutation<TClient, ErrorHelper, TUpdateClient>({
    mutationFn: async (clientData: TUpdateClient) => {
      if (!clientId) {
        throw new Error('Client ID is required for update')
      }
      return await ClientService.updateClient(clientId, clientData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['clients']})
    },
  })

  return {updateClient, isPending, isSuccess, error}
}

export const useSoftDeleteClient = () => {
  const queryClient = useQueryClient()
  const {
    mutate: softDeleteClient,
    isPending,
    error,
    isSuccess,
  } = useMutation<any, ErrorHelper, string>({
    mutationFn: async (clientId: string) => {
      await ClientService.softDeleteClient(clientId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['clients']})
    },
  })

  return {softDeleteClient, isPending, error, isSuccess}
}
